import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Svg, { Circle } from 'react-native-svg';
import { COLORS, FONTS } from '../constants/config';
import { KPIDistribution } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getTextAlign } from '../utils/rtl';
import { formatPct } from '../utils/formatPercentage';

import { tokens } from '@design';
interface KPIDistributionCardProps {
  distribution: KPIDistribution | undefined;
  title?: string;
}

/**
 * DistributionDonut - Multi-segment donut chart for KPI distribution
 */
const DistributionDonut = ({
  excellent,
  good,
  needsImprovement,
  size = 144,
  strokeWidth = 14,
}: {
  excellent: number;
  good: number;
  needsImprovement: number;
  size?: number;
  strokeWidth?: number;
}) => {
  const { colors, isDark } = useTheme();
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  // Calculate segment lengths
  const excellentLength = (excellent / 100) * circumference;
  const goodLength = (good / 100) * circumference;
  const needsImprovementLength = (needsImprovement / 100) * circumference;

  // Calculate offsets for each segment
  const excellentOffset = circumference - excellentLength;
  const goodOffset = excellentOffset - goodLength;
  const needsImprovementOffset = goodOffset - needsImprovementLength;

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["200"]}
          strokeWidth={strokeWidth}
          fill="none"
        />

        {/* Excellent segment (Green) */}
        {excellent > 0 && (
          <Circle
            cx={center}
            cy={center}
            r={radius}
            stroke={tokens.colors.raw.green["500"]}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${excellentLength} ${circumference}`}
            strokeDashoffset={excellentOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}

        {/* Good segment (Orange) */}
        {good > 0 && (
          <Circle
            cx={center}
            cy={center}
            r={radius}
            stroke={tokens.colors.raw.orange["500"]}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${goodLength} ${circumference}`}
            strokeDashoffset={goodOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}

        {/* Needs Improvement segment (Red) */}
        {needsImprovement > 0 && (
          <Circle
            cx={center}
            cy={center}
            r={radius}
            stroke={tokens.colors.raw.red["500"]}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${needsImprovementLength} ${circumference}`}
            strokeDashoffset={needsImprovementOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}
      </Svg>
    </View>
  );
};

/**
 * KPIDistributionCard - Displays KPI achievement distribution
 * Categories:
 * - ممتاز (Excellent): achievement >= 100% (Green)
 * - جيد (Good): achievement >= 80% and < 100% (Orange)
 * - يحتاج تحسين (Needs Improvement): achievement < 80% (Red)
 */
export default function KPIDistributionCard({ distribution, title }: KPIDistributionCardProps) {
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();

  // Use provided title or default to translated value
  const displayTitle = title ?? t('kpis.distribution');

  const hasData = distribution && distribution.total > 0;

  const categories = [
    {
      label: t('kpis.excellent'),
      percentage: hasData ? distribution.excellent : 0,
      count: hasData ? distribution.excellent_count : 0,
      color: tokens.colors.raw.green["500"], // Green
    },
    {
      label: t('kpis.good'),
      percentage: hasData ? distribution.good : 0,
      count: hasData ? distribution.good_count : 0,
      color: tokens.colors.raw.orange["500"], // Orange
    },
    {
      label: t('kpis.needsImprovement'),
      percentage: hasData ? distribution.needs_improvement : 0,
      count: hasData ? distribution.needs_improvement_count : 0,
      color: tokens.colors.raw.red["500"], // Red
    },
  ];

  return (
    <View style={[styles.container, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      {/* Title */}
      <View style={styles.titleContainer}>
        <Text style={[styles.title, { color: colors.textPrimary }]}>{displayTitle}</Text>
      </View>

      {/* Distribution Content */}
      <View style={styles.contentContainer}>
        {/* Left side - Donut chart */}
        <View style={styles.chartPlaceholder}>
          <DistributionDonut
            excellent={hasData ? distribution.excellent : 0}
            good={hasData ? distribution.good : 0}
            needsImprovement={hasData ? distribution.needs_improvement : 0}
            size={144}
            strokeWidth={14}
          />
        </View>

        {/* Right side - Categories */}
        <View style={styles.categoriesContainer}>
          {categories.map((category, index) => (
            <View key={index} style={styles.categoryItem}>
              {/* Color indicator bar */}
              <View style={[styles.colorDot, { backgroundColor: category.color }]} />

              {/* Label */}
              <Text style={[styles.categoryLabel, { color: colors.textPrimary }]}>
                {category.label}
              </Text>

              {/* Percentage badge */}
              <View style={[styles.countBadge, { backgroundColor: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["100"] }]}>
                <Text style={[styles.countText, { color: colors.textPrimary }]}>
                  %{formatPct(category.percentage)}
                </Text>
              </View>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  container: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["5"],
    height: '100%',
    elevation: 2,
    shadowColor: tokens.colors.raw.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
  },
  titleContainer: {
    marginBottom: tokens.spacing.absolute["5"],
  },
  title: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  contentContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["5"],
  },
  // Donut chart container
  chartPlaceholder: {
    width: 144,
    height: 144,
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoriesContainer: {
    flex: 1,
    gap: tokens.spacing.absolute["6"],
  },
  categoryItem: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    height: 32,
  },
  countBadge: {
    backgroundColor: tokens.colors.raw.slate["100"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1"],
    borderRadius: 100,
    minWidth: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  countText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
  },
  categoryLabel: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  colorDot: {
    width: 8,
    height: 32,
    borderRadius: tokens.radii.absolute["radius-4"],
  },
});

const styles = getStyles(true);
